import React, { useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Dialog,
    DialogContent,
    IconButton
} from "@mui/material";
import { FaArrowRight, FaArrowLeft, FaSearchPlus } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

const StateCatalog = ({ cursos, cursosImgs }) => {
    const [startIndex, setStartIndex] = useState(0);
    const [imagenAmpliada, setImagenAmpliada] = useState(null); // { src, alt } o null
    const cardsToShow = 3;

    const nextCards = () => {
        if (startIndex + cardsToShow < cursos.length) {
            setStartIndex(startIndex + 1);
        }
    };

    const prevCards = () => {
        if (startIndex > 0) {
            setStartIndex(startIndex - 1);
        }
    };

    const abrirImagen = (src, alt) => {
        setImagenAmpliada({ src, alt });
    };

    const cerrarImagen = () => {
        setImagenAmpliada(null);
    };

    if (!cursos || !cursosImgs || cursos.length === 0 || cursosImgs.length === 0) {
        return (
            <div>
                No hay cursos para recertificación por puntos autorizados por la
                Coordinación Nacional de Capacitación
            </div>
        );
    }

    const CardText = styled.p`
        font-family: "Oswald", serif;
        font-size: 18px;
        color: white;
        font-weight: 400;
        margin-bottom: 10px;
        text-align: center;
        text-shadow: 1px 0px 10px rgba(0, 0, 0, 0.5),
            -1px -1px 2px rgba(0, 0, 0, 0.2);
        margin: auto;
    `;

    const ZoomOverlay = styled.div`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 70px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0);
        opacity: 0;
        transition: all 0.2s ease-in-out;
        cursor: pointer;

        &:hover {
            background: rgba(0, 0, 0, 0.35);
            opacity: 1;
        }
    `;

    const MotionBox = motion(Box);

    return (
        <MotionBox
            sx={{ textAlign: "center", mt: 4, mb: 5 }}
            initial={{ opacity: 0, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    overflow: "hidden",
                    width: { xs: "90%", md: "80%" },
                    margin: "0 auto"
                }}
            >
                {/* Botón izquierdo */}
                <Button
                    onClick={prevCards}
                    variant="contained"
                    disabled={startIndex === 0}
                    sx={{
                        backgroundColor: "#12b854",
                        color: "white",
                        borderRadius: "10px",
                        "&:hover": { color: "white", backgroundColor: "#0d6834" },
                        width: { xs: "100%", md: "60px" },
                        height: { xs: "50px", md: "160px" },
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 0
                    }}
                >
                    <FaArrowLeft size={24} />
                </Button>

                {/* Carrusel */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        justifyContent: "center",
                        width: "100%"
                    }}
                >
                    {cursos.slice(startIndex, startIndex + cardsToShow).map((curso, index) => {
                        const imgSrc = cursosImgs[startIndex + index];
                        return (
                            <Card
                                key={index}
                                sx={{
                                    width: { xs: "90%", sm: "30%" },
                                    minWidth: "200px",
                                    textAlign: "center",
                                    boxShadow: 3,
                                    position: "relative"
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    height="300px"
                                    image={imgSrc}
                                    alt={curso}
                                />

                                {/* Overlay para ampliar imagen */}
                                <ZoomOverlay onClick={() => abrirImagen(imgSrc, curso)}>
                                    <FaSearchPlus color="white" size={32} />
                                </ZoomOverlay>

                                <CardContent
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        background:
                                            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 1%, rgba(0, 0, 0, 0.7) 50%)",
                                        color: "white",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                        height: "70px",
                                        padding: "0 16px",
                                        pointerEvents: "none"
                                    }}
                                >
                                    <CardText>{curso}</CardText>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>

                {/* Botón derecho */}
                <Button
                    onClick={nextCards}
                    variant="contained"
                    disabled={startIndex + cardsToShow >= cursos.length}
                    sx={{
                        backgroundColor: "#12b854",
                        color: "white",
                        borderRadius: "10px",
                        "&:hover": { color: "white", backgroundColor: "#0d6834" },
                        width: { xs: "100%", md: "60px" },
                        height: { xs: "50px", md: "160px" },
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 0
                    }}
                >
                    <FaArrowRight size={24} />
                </Button>
            </Box>

            {/* Modal / Lightbox con la imagen completa */}
            <Dialog
                open={Boolean(imagenAmpliada)}
                onClose={cerrarImagen}
                maxWidth="md"
                fullWidth
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#000"
                    }}
                >
                    <IconButton
                        onClick={cerrarImagen}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(255,255,255,0.85)",
                            "&:hover": { backgroundColor: "#fff" },
                            zIndex: 1
                        }}
                    >
                        <MdClose size={22} />
                    </IconButton>
                    {imagenAmpliada && (
                        <img
                            src={imagenAmpliada.src}
                            alt={imagenAmpliada.alt}
                            style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: "90vh",
                                objectFit: "contain",
                                display: "block"
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </MotionBox>
    );
};

export default StateCatalog;